import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { insertLog } from "../services/logService";

// ── Shape raw Supabase row → app format ──
const shapeOpp = (o) => {
  // Extract multi-assignees
  const assignees = (o.opportunities_assignees || []).map(item => {
    const e = item.employees;
    if (!e) return null;
    return {
      id:       e.id,
      name:     e.full_name || "",
      color:    "#3761E9",
      initials: (e.full_name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2),
      status:   e.status === "Active" ? "online" : "offline",
      position: e.job_title || "",
      avatar:   e.avatar_url || null,
    };
  }).filter(Boolean);

  return {
    id:             o.id,
    name:           o.name            || "",
    company:        o.company         || "",
    contact:        o.contact         || "",
    phone:          o.phone           || "",
    email:          o.email           || "",
    notes:          o.notes           || "",
    stage:          o.stage           || "New",
    pipeline:       o.pipeline        || "",
    priority:       o.priority        || "Medium",
    source:         o.source          || "",
    probability:    Number(o.probability) || 0,
    archived:       o.archived        || false,
    repeatCustomer: o.repeat_customer || false,
    referralName:   o.referral_name  || "",
    referralPhone:  o.referral_phone || "",
    referralRate:   o.referral_rate  ? Number(o.referral_rate) : 0,
    tags: (o.opportunity_tags || []).map(t => ({
      label: t.custom_label || t.label || "",
      color: t.color || "#757575",
    })),
    assignee: o.assignee_emp ? {
      id:       o.assignee_emp.id,
      name:     o.assignee_emp.full_name || "",
      color:    "#3761E9",
      initials: (o.assignee_emp.full_name || "?")
                  .split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2),
      status:   o.assignee_emp.status === "Active" ? "online" : "offline",
      position: o.assignee_emp.job_title || "",
      avatar:   o.assignee_emp.avatar_url || null,
    } : null,
    assignees: assignees,
    amount:        o.amount       ? parseFloat(o.amount) : null,
    startDate:     o.start_date   || "",
    endDate:       o.end_date     || "",
    contactMethod: o.contact_method || "",
    attachments:   [],
    activities:    null,
  };
};

// ── Only send known DB columns ──
const toDbRow = (data) => ({
  name:            data.name           || "",
  company:         data.company        || null,
  contact:         data.contact        || null,
  phone:           data.phone          || null,
  email:           data.email          || null,
  notes:           data.notes          || null,
  stage:           data.stage          || "New",
  pipeline:        data.pipeline       || null,
  priority:        data.priority       || "Medium",
  source:          data.source === "Other"
                     ? (data.sourceOther || "Other")
                     : (data.source || null),
  probability:     data.probability != null ? parseInt(data.probability) : null,
  amount:          data.amount         ? parseFloat(data.amount) : null,
  start_date:      data.startDate      || null,
  end_date:        data.endDate        || null,
  contact_method:  data.contactMethod  || null,
  repeat_customer: data.repeatCustomer || false,
  referral_name:   data.referralName   || null,
  referral_phone:  data.referralPhone  || null,
  referral_rate:   data.referralRate   ? Number(data.referralRate) : null,
  assignee_id:     (data.assignees && data.assignees[0]?.id) 
                     ? data.assignees[0].id 
                     : (data.assignee?.id || null),
  archived:        data.archived       || false,
});

export function useOpportunities() {
  const [opps,    setOpps]    = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Fetch all ──
  const fetchAll = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("opportunities")
        .select(`
          *,
          assignee_emp:assignee_id (id, full_name, job_title, avatar_url, status),
          opportunities_assignees (
            employees:employee_id (id, full_name, job_title, avatar_url, status)
          ),
          opportunity_tags (label, color, custom_label)
        `)
        .order("created_at", { ascending: false });

      if (error) {
        console.warn("[CRM] fetch:", error.message, "| code:", error.code);
        return;
      }

      const shaped = (data || []).map(shapeOpp);
      setOpps(shaped);
    } catch (err) {
      console.warn("[CRM] fetch error:", err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const ch = supabase
      .channel("opps_realtime_v3")
      .on("postgres_changes", { event: "*", schema: "public", table: "opportunities" }, fetchAll)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [fetchAll]);

  // ── Create ──
  const createOpp = useCallback(async (data) => {
    const row = toDbRow(data);

    const { data: created, error } = await supabase
      .from("opportunities")
      .insert(row)
      .select()
      .single();

    if (error) {
      console.error("[CRM] createOpp failed:", error.message, error.details, error.hint);
      throw new Error(error.message);
    }

    // Tags
    const tags = (data.tags || []).filter(t => t.label);
    if (tags.length) {
      await supabase.from("opportunity_tags").insert(
        tags.map(t => ({
          opportunity_id: created.id,
          label: t.label === "Other" ? (data.tagOther || "Other") : t.label,
          color: t.color || "#757575",
        }))
      ).then(({ error: te }) => {
        if (te) console.warn("[CRM] tags insert:", te.message);
      });
    }

    await insertLog({
      action:     "CREATE_OPPORTUNITY",
      actor_name: data.assignee?.name || "System",
      details:    `Created opportunity: "${data.name}"`,
      timestamp:  new Date().toISOString(),
      module:     "CRM",
    }).catch(() => {});

    await fetchAll();
    return created;
  }, [fetchAll]);

  // ── Update ──
  const updateOpp = useCallback(async (data) => {
    const { id } = data;
    if (!id) return;

    const row = toDbRow(data);

    // 1. Update Tags (Auxiliary)
    await supabase.from("opportunity_tags").delete().eq("opportunity_id", id);
    const tags = (data.tags || []).filter(t => t.label);
    if (tags.length) {
      await supabase.from("opportunity_tags").insert(
        tags.map(t => ({ opportunity_id: id, label: t.label, color: t.color || "#757575" }))
      );
    }

    // 2. Update Assignees (Auxiliary)
    const newAssignees = data.assignees !== undefined 
      ? data.assignees 
      : (data.assignee ? [data.assignee] : null);

    if (newAssignees !== null) {
      // Delete old
      const { error: delErr } = await supabase.from("opportunities_assignees").delete().eq("opportunity_id", id);
      if (delErr) console.warn("[CRM] delete assignees error:", delErr.message);
      
      // Insert new
      if (newAssignees.length > 0) {
        // Deduplicate assignees based on employee ID to prevent PK violations
        const uniqueAssigneeIds = [...new Set(newAssignees.map(a => a.id))];
        const assigneeRows = uniqueAssigneeIds.map(empId => ({
          opportunity_id: id,
          employee_id: empId,
        }));
        
        const { error: assignError } = await supabase.from("opportunities_assignees").insert(assigneeRows);
        if (assignError) {
           console.warn("[CRM] update assignees error:", assignError.message);
           alert(`Failed to update assignees: ${assignError.message}`);
        }
      }
    }

    // 3. Update Main Table (Triggers Realtime)
    const { error } = await supabase
      .from("opportunities")
      .update(row)
      .eq("id", id);

    if (error) {
      console.error("[CRM] updateOpp failed:", error.message);
      alert(`Update failed: ${error.message}`);
      return;
    }

    // Security Log
    await insertLog({
      action:     "UPDATE_OPPORTUNITY",
      actor_name: data.assignee?.name || "System",
      details:    `Updated opportunity: "${data.name}"`,
      timestamp:  new Date().toISOString(),
      module:     "CRM",
    }).catch(() => {});

    await fetchAll();
  }, [fetchAll]);

  // ── Delete ──
  const deleteOpp = useCallback(async (id) => {
    const { data: found } = await supabase
      .from("opportunities").select("name").eq("id", id).single();

    await supabase.from("opportunity_tags").delete().eq("opportunity_id", id);
    const { error } = await supabase.from("opportunities").delete().eq("id", id);
    if (error) throw new Error(error.message);

    await insertLog({
      action:     "DELETE_OPPORTUNITY",
      actor_name: "System",
      details:    `Deleted opportunity: "${found?.name || id}"`,
      timestamp:  new Date().toISOString(),
      module:     "CRM",
    }).catch(() => {});

    setOpps(prev => prev.filter(o => o.id !== id));
  }, []);

  // ── Archive ──
  const archiveOpp = useCallback(async (opp) => {
    const { error } = await supabase
      .from("opportunities")
      .update({ archived: true, archived_at: new Date().toISOString() })
      .eq("id", opp.id);

    if (!error) {
      await insertLog({
        action: "ARCHIVE_OPPORTUNITY", actor_name: "System",
        details: `Archived: "${opp.name}"`, timestamp: new Date().toISOString(), module: "CRM",
      }).catch(() => {});
      setOpps(prev => prev.map(o => o.id === opp.id ? { ...o, archived: true } : o));
    }
  }, []);

  // ── Restore ──
  const restoreOpp = useCallback(async (opp) => {
    const { error } = await supabase
      .from("opportunities")
      .update({ archived: false, archived_at: null })
      .eq("id", opp.id);

    if (!error) {
      await insertLog({
        action: "RESTORE_OPPORTUNITY", actor_name: "System",
        details: `Restored: "${opp.name}"`, timestamp: new Date().toISOString(), module: "CRM",
      }).catch(() => {});
      setOpps(prev => prev.map(o => o.id === opp.id ? { ...o, archived: false } : o));
    }
  }, []);

  // ── Auto-create project when deal is Closed Won ──
  const autoCreateProject = useCallback(async (opp) => {
    try {
      // Check if a project already exists for this opportunity
      const { data: existing } = await supabase
        .from("projects")
        .select("id")
        .eq("crm_opportunity_id", opp.id)
        .maybeSingle();

      if (existing) {
        console.log("[CRM→Projects] Project already exists for opportunity:", opp.id);
        return;
      }

      // Create the project automatically (carry referral data from CRM deal)
      const { error } = await supabase.from("projects").insert({
        title:                opp.name     || "New Project",
        client_name:          opp.company  || opp.contact || "",
        total_contract_value: opp.amount   || 0,
        status:               "Ongoing",
        start_date:           opp.startDate || new Date().toISOString().split("T")[0],
        delivery_date:        opp.endDate  || null,
        crm_opportunity_id:   opp.id,
        referral_name:        opp.referralName  || null,
        referral_phone:       opp.referralPhone || null,
        referral_rate:        opp.referralRate  || null,
        created_at:           new Date().toISOString(),
      });

      if (error) {
        console.warn("[CRM→Projects] Auto-create failed:", error.message);
      } else {
        console.log("[CRM→Projects] ✅ Project auto-created for:", opp.name);
        await insertLog({
          action: "AUTO_CREATE_PROJECT", actor_name: "System",
          details: `Auto-created project from CRM deal: "${opp.name}" (Closed Won)`,
          timestamp: new Date().toISOString(), module: "Projects",
        }).catch(() => {});
      }
    } catch (err) {
      console.warn("[CRM→Projects] Error:", err.message);
    }
  }, []);

  // ── Stage Change ──
  const changeStage = useCallback(async (id, stage, currentUser) => {
    // Handle numeric IDs (SEED DATA) gracefully
    if (typeof id === 'number' || (typeof id === 'string' && !isNaN(id))) {
      console.warn("Skipping DB stage change for seed opportunity:", id);
      setOpps(prev => prev.map(o => o.id === id ? { ...o, stage } : o));
      return;
    }

    // 1. Find old stage for logging and rollback
    const oldOpp = opps.find(o => o.id === id);
    if (!oldOpp) return;
    const oldStage = oldOpp.stage || "Unknown";

    // Prevent unnecessary updates if stage is same
    if (oldStage === stage) return;

    // 2. Optimistic Update (Update Local State Immediately)
    setOpps(prev => prev.map(o => o.id === id ? { ...o, stage } : o));

    try {
      // 3. Update DB
      const { error } = await supabase.from("opportunities").update({ stage }).eq("id", id);

      if (error) {
        throw error; // Trigger catch block to revert
      }

      // 4. Auto-create project if stage = "Closed Won"
      if (stage === "Closed Won") {
        const updatedOpp = { ...oldOpp, stage };
        await autoCreateProject(updatedOpp);
      }

      // 5. Log to Activity Feed (History inside project)
      if (currentUser) {
        const activity = {
          opportunity_id: id,
          type: 'stage_change',
          content: `${oldStage}->${stage}`, // Format expected by ActivityFeed
          created_by: currentUser.id,
          user_name: `${currentUser.name}${currentUser.employee_number ? ` (ID: ${currentUser.employee_number})` : ""}`,
          created_at: new Date().toISOString(),
        };
        const { error: actError } = await supabase.from("crm_activities").insert(activity);
        if (actError) console.warn("[CRM] log stage change error:", actError.message);
      }
    } catch (err) {
      console.error("[CRM] changeStage failed, reverting:", err.message);
      // Revert Local State
      setOpps(prev => prev.map(o => o.id === id ? { ...o, stage: oldStage } : o));
    }
  }, [opps, autoCreateProject]);

  return {
    opps, loading,
    createOpp, updateOpp, deleteOpp, archiveOpp, restoreOpp, changeStage,
    refetch: fetchAll,
  };
}
